import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent, FormControlLabel, IconButton,
    MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, getBetupFeedChannelCategories, getBetupFeedChannelOverview, getBetupFeedChannelSubscriberStats,
    getBetupFeedChannelTimeline, getPaginatedSports,
} from '../Api';
import {
    BETUP_FEED_CHANNEL_LANGUAGES, BetupFeedChannelCategoryDto, BetupFeedChannelDto, BetupFeedChannelLanguage,
    BetupFeedChannelOverviewDto, BetupFeedChannelPostDto, BetupFeedChannelPostStatus, BetupFeedChannelSubscriberDayDto,
} from '../models/dtos/BetupFeedChannelDto';
import SportEntityDto from '../models/dtos/SportEntityDto';
import { formatCount, formatFeedDate, loadErrorText } from '../components/betupFeed/BetupFeedShared';
import {
    categoryName, ChannelDialog, channelLanguages, ChannelLogo, channelName, ChannelPostDialog, LANGUAGE_LABELS,
    LanguageChips, TopChannelChips,
} from '../components/betupFeed/BetupFeedChannelDialogs';
import ChannelPostCard, { postTextIn } from '../components/betupFeed/ChannelPostCard';
import WriteArticleDialog from '../components/publishing/WriteArticleDialog';
import ChannelArticleExecutionDialog, { ArticleStageChip } from '../components/publishing/ChannelArticleExecutionDialog';
import { getChannelArticleRunning } from '../Api';
import { ChannelArticleExecutionDto } from '../models/dtos/PublishingDto';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChannelSubscriberDrawer, { relativeTime, SubscriberDrawerEntry } from '../components/betupFeed/ChannelSubscriberDrawer';
import { cardPreview } from '../components/betupFeed/postWidgets';
import { SUBSCRIBED_COLOR, UNSUBSCRIBED_COLOR } from '../components/betupFeed/ChannelSubscriberChart';

const PAGE_SIZE = 20;
const STATUSES: { value: BetupFeedChannelPostStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'published', label: 'Published' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'draft', label: 'Drafts' },
];
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const TOP_MIN_SUBSCRIBERS = '1,000';

function todayIn(tz: string): string {
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch {
        return new Date().toISOString().slice(0, 10);
    }
}

function daysBefore(day: string, days: number): string {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
}

function signed(value: number): string {
    return value > 0 ? `+${formatCount(value)}` : value < 0 ? `−${formatCount(-value)}` : '0';
}

function perPost(total: number, posts: number): string {
    return posts > 0 ? (total / posts).toFixed(1) : '—';
}

/**
 * BETUP Feed › Channels › a channel: its figures now (subscribers, posting, engagement, premium sales, top posts) and
 * every post newest first in an endless list; a card expands in place into the full article with its widgets, who
 * liked it and its comments (deletable); a player's name opens their card.
 */
export default function BetupFeedChannelPage() {
    const { id } = useParams();
    const channelId = Number(id);
    const [searchParams, setSearchParams] = useSearchParams();
    const status = (STATUSES.find(s => s.value === searchParams.get('status'))?.value ?? 'all') as BetupFeedChannelPostStatus;
    const showDeleted = searchParams.get('deleted') === '1';
    const langParam = searchParams.get('lang');
    const lang = (BETUP_FEED_CHANNEL_LANGUAGES as readonly string[]).includes(langParam ?? '') ? langParam as BetupFeedChannelLanguage : null;

    const update = React.useCallback((changes: Record<string, string | null>) => {
        setSearchParams(current => {
            const next = new URLSearchParams(current);
            Object.entries(changes).forEach(([key, value]) => value == null ? next.delete(key) : next.set(key, value));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // ---- Overview ----
    const [overview, setOverview] = React.useState<BetupFeedChannelOverviewDto | null>(null);
    const [overviewError, setOverviewError] = React.useState<string | null>(null);
    const [trend, setTrend] = React.useState<BetupFeedChannelSubscriberDayDto[] | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);

    // ---- AI article (docs/channel-articles.md): Write article, and an invocation of this channel in progress ----
    const [writeOpen, setWriteOpen] = React.useState(false);
    const [running, setRunning] = React.useState<ChannelArticleExecutionDto | null>(null);
    const [runningOpen, setRunningOpen] = React.useState(false);
    const [watchUntil, setWatchUntil] = React.useState(0);
    const loadRunning = React.useCallback(() => getChannelArticleRunning(channelId).then(setRunning), [channelId]);
    React.useEffect(() => { loadRunning(); }, [loadRunning]);
    React.useEffect(() => {
        if (!running && Date.now() > watchUntil) return;
        const timer = setInterval(() => {
            if (!running && Date.now() > watchUntil) clearInterval(timer);
            loadRunning();
        }, 5_000);
        return () => clearInterval(timer);
    }, [running, watchUntil, loadRunning]);

    const loadOverview = React.useCallback(async () => {
        const result = await getBetupFeedChannelOverview(channelId);
        setOverview(result);
        setOverviewError(result == null ? loadErrorText(BETUP_FEED_LABELS.channelOverview, 'Failed to load the channel.') : null);
        const to = todayIn(BROWSER_TZ);
        const stats = await getBetupFeedChannelSubscriberStats(channelId, { from: daysBefore(to, 29), to, tz: BROWSER_TZ });
        setTrend(stats?.days ?? null);
    }, [channelId]);

    React.useEffect(() => { loadOverview(); }, [loadOverview]);
    const channel = overview?.channel ?? null;

    // ---- Timeline (keyset-paged, endless) ----
    const [posts, setPosts] = React.useState<BetupFeedChannelPostDto[]>([]);
    const [cursor, setCursor] = React.useState<string | null>(null);
    const [done, setDone] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [listError, setListError] = React.useState<string | null>(null);
    const generation = React.useRef(0);
    const loadingRef = React.useRef(false);

    const loadMore = React.useCallback(async (reset = false) => {
        if (loadingRef.current && !reset) return;
        const myGeneration = reset ? ++generation.current : generation.current;
        loadingRef.current = true;
        setLoading(true);
        const page = await getBetupFeedChannelTimeline(channelId, reset ? null : cursor, PAGE_SIZE, status, showDeleted);
        if (myGeneration !== generation.current) return; // filters changed meanwhile
        loadingRef.current = false;
        setLoading(false);
        if (page == null) {
            setListError(loadErrorText(BETUP_FEED_LABELS.channelTimeline, 'Failed to load the posts.'));
            return;
        }
        setListError(null);
        setPosts(prev => {
            const base = reset ? [] : prev;
            const seen = new Set(base.map(p => p.id));
            return [...base, ...page.items.filter(p => !seen.has(p.id))];
        });
        setCursor(page.next_cursor);
        setDone(page.next_cursor == null);
    }, [channelId, cursor, status, showDeleted]);

    const reloadTimeline = React.useCallback(() => {
        setPosts([]);
        setCursor(null);
        setDone(false);
        loadingRef.current = false;
        loadMore(true);
    }, [loadMore]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => { reloadTimeline(); }, [channelId, status, showDeleted]);

    const sentinel = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const element = sentinel.current;
        if (element == null || done) return;
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !loadingRef.current && !listError) loadMore();
        }, { rootMargin: '600px 0px' });
        observer.observe(element);
        return () => observer.disconnect();
    }, [loadMore, done, listError, posts.length]);

    // ---- A top post: expanded in place when it is in the list, else on its own ----
    const [expandedId, setExpandedId] = React.useState<number | null>(null);
    const [topPost, setTopPost] = React.useState<BetupFeedChannelPostDto | null>(null);
    const cardRefs = React.useRef(new Map<number, HTMLDivElement>());
    const showTopPost = (post: BetupFeedChannelPostDto) => {
        const element = cardRefs.current.get(post.id);
        if (element) {
            setExpandedId(post.id);
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            setTopPost(post);
        }
    };

    // ---- A player's card (likes, comments) ----
    const [player, setPlayer] = React.useState<SubscriberDrawerEntry | null>(null);
    const openPlayer = React.useCallback((userId: number, known: SubscriberDrawerEntry['player']) => setPlayer({ userId, player: known }), []);

    // ---- Editing (the channels page's dialogs) ----
    const [editingPost, setEditingPost] = React.useState<{ post: BetupFeedChannelPostDto | null } | null>(null);
    const [editingChannel, setEditingChannel] = React.useState(false);
    const [categories, setCategories] = React.useState<BetupFeedChannelCategoryDto[]>([]);
    const [sports, setSports] = React.useState<SportEntityDto[] | null>([]);
    React.useEffect(() => {
        getBetupFeedChannelCategories().then(list => setCategories(list ?? []));
        getPaginatedSports(0, 500).then(result => setSports(result?.items ?? null)).catch(() => setSports(null));
    }, []);

    const replacePost = (changed: BetupFeedChannelPostDto) =>
        setPosts(prev => prev.map(p => (p.id === changed.id ? { ...p, ...changed } : p)));

    const afterPostChanged = (changed: BetupFeedChannelPostDto, message: string) => {
        setNotice(message);
        replacePost(changed);
        loadOverview();
    };

    const afterPostSaved = (saved: BetupFeedChannelPostDto | null, created: boolean, draft: boolean) => {
        setEditingPost(null);
        setNotice(created ? `Post ${saved ? `#${saved.id} ` : ''}${draft ? 'saved as a draft.' : 'published.'}` : `Post ${saved ? `#${saved.id} ` : ''}saved.`);
        if (created || saved == null) reloadTimeline(); else replacePost(saved);
        loadOverview();
    };

    const o = overview;
    const subscribersNow = o?.subscribers.now ?? 0;
    const languages = channel ? channelLanguages(channel) : [];
    const categoryById = new Map(categories.map(c => [c.id, c]));

    return (
        <Stack gap={2.5} sx={{ mx: { xs: 2, md: 5 }, my: 4 }}>
            {/* Header */}
            <Stack direction="row" alignItems="flex-start" gap={1.5} flexWrap="wrap">
                <IconButton component={RouterLink} to="/marketing/betup-feed/channels" aria-label="Back to channels"><ArrowBackIcon /></IconButton>
                {channel && <ChannelLogo channel={channel} size={56} />}
                <Stack sx={{ flex: 1, minWidth: 240 }}>
                    <Typography variant="h4">{channel ? channelName(channel) : `Channel #${channelId}`}</Typography>
                    {channel && (
                        <>
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">#{channel.id}</Typography>
                                {channel.active ? <Chip size="small" color="success" label="Active" /> : <Chip size="small" variant="outlined" label="Inactive" />}
                                <TopChannelChips channel={channel} />
                                {languages.length > 0 && <LanguageChips languages={languages} />}
                                {channel.category_ids.map(cid => (
                                    <Chip key={cid} size="small" variant="outlined" label={categoryById.get(cid) ? categoryName(categoryById.get(cid)!) : `category #${cid}`} />
                                ))}
                                {channel.model && <Chip size="small" variant="outlined" label={channel.model} />}
                            </Stack>
                            {channel.descriptions?.en && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 820, whiteSpace: 'pre-wrap' }}>{channel.descriptions.en}</Typography>
                            )}
                        </>
                    )}
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                    <Tooltip title="Reload"><IconButton onClick={() => { loadOverview(); reloadTimeline(); }}><RefreshIcon /></IconButton></Tooltip>
                    <Button variant="outlined" component={RouterLink} to={`/marketing/betup-feed/channels/${channelId}/subscribers`}>Subscribers</Button>
                    <Button variant="outlined" disabled={!channel} onClick={() => setEditingChannel(true)}>Edit channel</Button>
                    {running && (
                        <Tooltip title={`AI article in progress (invocation #${running.id})`}>
                            <span><ArticleStageChip status={running.status} /></span>
                        </Tooltip>
                    )}
                    {running && <Button size="small" onClick={() => setRunningOpen(true)}>Open</Button>}
                    <Button variant="outlined" component={RouterLink} to={`/marketing/betup-feed/publishing?tab=invocations&channel=${channelId}`}>AI articles</Button>
                    <Button variant="outlined" startIcon={<AutoAwesomeIcon />} disabled={!channel || running != null} onClick={() => setWriteOpen(true)}>Write article</Button>
                    <Button variant="contained" disabled={!channel} onClick={() => setEditingPost({ post: null })}>New post</Button>
                </Stack>
            </Stack>

            {overviewError && <Alert severity="error" onClose={() => setOverviewError(null)}>{overviewError}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            {/* Figures now */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 2 }}>
                <Tile label="Subscribers" value={o ? formatCount(subscribersNow) : '—'}
                    detail={o ? `${subscribersNow > 0 ? Math.round((o.subscribers.notifications_enabled / subscribersNow) * 100) : 0}% get pushes` : ''}
                    to={`/marketing/betup-feed/channels/${channelId}/subscribers`} />
                <Tile label="Last 7 days" value={o ? signed(o.subscribers.subscribed_7d - o.subscribers.unsubscribed_7d) : '—'}
                    detail={o ? <><Dot color={SUBSCRIBED_COLOR} />+{formatCount(o.subscribers.subscribed_7d)} <Dot color={UNSUBSCRIBED_COLOR} />−{formatCount(o.subscribers.unsubscribed_7d)}</> : ''} />
                <Tile label="Last 30 days" value={o ? signed(o.subscribers.subscribed_30d - o.subscribers.unsubscribed_30d) : '—'}
                    detail={o ? <><Dot color={SUBSCRIBED_COLOR} />+{formatCount(o.subscribers.subscribed_30d)} <Dot color={UNSUBSCRIBED_COLOR} />−{formatCount(o.subscribers.unsubscribed_30d)}</> : ''} />
                <Tile label="Posts" value={o ? formatCount(o.posts.published) : '—'}
                    detail={o ? `${formatCount(o.posts.published_7d)} this week · ${formatCount(o.posts.published_30d)} in 30 days` : ''} />
                <Tile label="Engagement (30 days)" value={o ? `${perPost(o.engagement.likes_30d + o.engagement.comments_30d, o.posts.published_30d)}` : '—'}
                    detail={o ? `per post · ${formatCount(o.engagement.likes)} likes · ${formatCount(o.engagement.comments)} comments${o.engagement.views != null ? ` · ${formatCount(o.engagement.views)} views` : ''} in all` : ''} />
                <Tile label="Premium sales" value={o ? formatCount(o.sales.betcoins) : '—'}
                    detail={o ? `betcoins · ${formatCount(o.sales.count)} sales to ${formatCount(o.sales.buyers)} players · ${formatCount(o.posts.premium)} premium posts` : ''} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 3, alignItems: 'start' }}>
                {/* The timeline */}
                <Stack gap={2} sx={{ width: '100%', maxWidth: 640, justifySelf: { lg: 'center' } }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, value) => value && update({ status: value === 'all' ? null : value })}>
                            {STATUSES.map(s => <ToggleButton key={s.value} value={s.value}>{s.label}</ToggleButton>)}
                        </ToggleButtonGroup>
                        <FormControlLabel control={<Switch size="small" checked={showDeleted} onChange={e => update({ deleted: e.target.checked ? '1' : null })} />} label="Deleted" />
                        <Box sx={{ flex: 1 }} />
                        <TextField select size="small" label="Read in" value={lang ?? ''} sx={{ minWidth: 150 }}
                            onChange={e => update({ lang: e.target.value || null })}>
                            <MenuItem value="">Auto (English first)</MenuItem>
                            {(languages.length > 0 ? languages : BETUP_FEED_CHANNEL_LANGUAGES).map(l => <MenuItem key={l} value={l}>{LANGUAGE_LABELS[l]}</MenuItem>)}
                        </TextField>
                    </Stack>

                    {posts.map(post => (
                        <Box key={post.id} ref={(el: HTMLDivElement | null) => { if (el) cardRefs.current.set(post.id, el); else cardRefs.current.delete(post.id); }}
                            sx={{ scrollMarginTop: 16 }}>
                            <ChannelPostCard post={post} channel={channel} lang={lang} defaultExpanded={expandedId === post.id}
                                onEdit={channel ? p => setEditingPost({ post: p }) : null}
                                onChanged={afterPostChanged} onOpenPlayer={openPlayer} />
                        </Box>
                    ))}

                    {listError && (
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { setListError(null); loadMore(posts.length === 0); }}>Retry</Button>}>
                            {listError}
                        </Alert>
                    )}
                    <Box ref={sentinel} sx={{ height: 1 }} />
                    {loading && <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={28} /></Stack>}
                    {done && !loading && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            {posts.length === 0 ? 'No posts here yet.' : `That's all — ${formatCount(posts.length)} post${posts.length === 1 ? '' : 's'}.`}
                        </Typography>
                    )}
                </Stack>

                {/* Sidebar */}
                <Stack gap={2} sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Subscribers, 30 days</Typography>
                                <Button size="small" component={RouterLink} to={`/marketing/betup-feed/channels/${channelId}/subscribers`}>Details</Button>
                            </Stack>
                            {trend && trend.length > 1 ? <Sparkline days={trend} /> : <Typography variant="body2" color="text.secondary">No data yet.</Typography>}
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Publishing</Typography>
                            <Stack gap={0.75}>
                                <Fact label="Last post" value={o?.posts.last_published_at ? `${relativeTime(o.posts.last_published_at)}` : '—'} hint={formatFeedDate(o?.posts.last_published_at)} />
                                <Fact label="Next scheduled" value={o?.posts.next_scheduled_at ? relativeTime(o.posts.next_scheduled_at) : 'none'} hint={o?.posts.next_scheduled_at ? formatFeedDate(o.posts.next_scheduled_at) : undefined} />
                                <Fact label="Drafts" value={formatCount(o?.posts.drafts)} action={o && o.posts.drafts > 0 ? () => update({ status: 'draft' }) : undefined} />
                                <Fact label="Scheduled" value={formatCount(o?.posts.scheduled)} action={o && o.posts.scheduled > 0 ? () => update({ status: 'scheduled' }) : undefined} />
                                <Fact label="Deleted" value={formatCount(o?.posts.deleted)} action={o && o.posts.deleted > 0 ? () => update({ deleted: '1' }) : undefined} />
                                <Fact label="Sales, 30 days" value={o ? `${formatCount(o.sales.count_30d)} · ${formatCount(o.sales.betcoins_30d)} betcoins` : '—'} />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Top posts, 30 days</Typography>
                            <Typography variant="caption" color="text.secondary">By likes + comments</Typography>
                            <Stack gap={1} sx={{ mt: 1 }}>
                                {(o?.top_posts.length ?? 0) === 0 && <Typography variant="body2" color="text.secondary">No published posts in 30 days.</Typography>}
                                {o?.top_posts.map((post, i) => (
                                    <Box key={post.id} onClick={() => showTopPost(post)}
                                        sx={{ cursor: 'pointer', p: 1, mx: -1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                        <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            <b>{i + 1}.</b> {cardPreview(postTextIn(post, lang).text).text || `Post #${post.id}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ♥ {formatCount(post.likes_count)} · 💬 {formatCount(post.comments_count)}{post.views_count != null ? ` · 👁 ${formatCount(post.views_count)}` : ''} · {relativeTime(post.created_at)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Box>

            <Dialog open={topPost != null} onClose={() => setTopPost(null)} maxWidth="md" fullWidth>
                <DialogContent sx={{ p: 2 }}>
                    {topPost && (
                        <ChannelPostCard post={topPost} channel={channel} lang={lang} defaultExpanded
                            onEdit={channel ? p => { setTopPost(null); setEditingPost({ post: p }); } : null}
                            onChanged={(changed, message) => { setTopPost(changed); afterPostChanged(changed, message); }}
                            onOpenPlayer={openPlayer} />
                    )}
                </DialogContent>
            </Dialog>
            <ChannelSubscriberDrawer entry={player} position={null} onClose={() => setPlayer(null)} onPrevious={null} onNext={null} />
            {channel && (
                <ChannelPostDialog
                    open={editingPost != null}
                    channel={channel}
                    post={editingPost?.post ?? null}
                    onClose={() => setEditingPost(null)}
                    onSaved={afterPostSaved}
                    onEditChannel={() => setEditingChannel(true)}
                />
            )}
            <ChannelDialog
                open={editingChannel}
                channel={channel as BetupFeedChannelDto | null}
                categories={categories}
                sports={sports}
                topMinSubscribers={TOP_MIN_SUBSCRIBERS}
                onClose={() => setEditingChannel(false)}
                onSaved={() => { setEditingChannel(false); setNotice('Channel saved.'); loadOverview(); }}
            />
            <WriteArticleDialog open={writeOpen} channels={[]} channelId={channelId} matchId={null} onClose={() => setWriteOpen(false)}
                onQueued={(_, matchId) => {
                    setWriteOpen(false);
                    setNotice(`Queued: an AI article${matchId ? ` about match ${matchId}` : ' about the channel\'s best upcoming match'}. It appears here as a draft when it is done.`);
                    setWatchUntil(Date.now() + 3 * 60_000);
                    setTimeout(loadRunning, 1500);
                }} />
            <ChannelArticleExecutionDialog executionId={runningOpen && running ? running.id : null}
                onClose={() => { setRunningOpen(false); loadRunning(); reloadTimeline(); }} />
        </Stack>
    );
}

function Dot({ color }: { color: string }) {
    return <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', bgcolor: color, mr: 0.5, ml: 0.5 }} />;
}

function Tile({ label, value, detail, to }: { label: string; value: string; detail?: React.ReactNode; to?: string }) {
    const body = (
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, my: 0.25 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">{detail || ' '}</Typography>
        </CardContent>
    );
    return (
        <Card variant="outlined" sx={to ? { '&:hover': { borderColor: 'primary.main' } } : undefined}>
            {to ? <Box component={RouterLink} to={to} sx={{ color: 'inherit', textDecoration: 'none', display: 'block' }}>{body}</Box> : body}
        </Card>
    );
}

function Fact({ label, value, hint, action }: { label: string; value: React.ReactNode; hint?: string; action?: () => void }) {
    return (
        <Stack direction="row" gap={1.5} alignItems="baseline">
            <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>{label}</Typography>
            <Tooltip title={hint ?? ''}>
                <Typography variant="body2" onClick={action}
                    sx={action ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}>
                    {value}
                </Typography>
            </Tooltip>
        </Stack>
    );
}

/** Total subscribers at the end of each of the last 30 days, with a hover readout. */
function Sparkline({ days }: { days: BetupFeedChannelSubscriberDayDto[] }) {
    const [hover, setHover] = React.useState<number | null>(null);
    const width = 320;
    const height = 72;
    const totals = days.map(d => d.total);
    const lo = Math.min(...totals);
    const hi = Math.max(...totals);
    const x = (i: number) => (i * width) / (days.length - 1);
    const y = (v: number) => 6 + (1 - (v - lo) / Math.max(1, hi - lo)) * (height - 12);
    const shown = hover != null ? days[hover] : days[days.length - 1];
    const first = days[0].total;
    return (
        <Box>
            <Stack direction="row" alignItems="baseline" gap={1} sx={{ mt: 0.5 }}>
                <Typography variant="h6">{formatCount(shown.total)}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {hover != null ? shown.date : `${signed(shown.total - first)} in 30 days`}
                </Typography>
            </Stack>
            <svg viewBox={`0 0 ${width} ${height}`} width="100%" onMouseLeave={() => setHover(null)} role="img" aria-label="Subscribers over the last 30 days">
                <polyline points={days.map((d, i) => `${x(i)},${y(d.total)}`).join(' ')} fill="none" stroke={SUBSCRIBED_COLOR} strokeWidth={2} strokeLinejoin="round" />
                {hover != null && <circle cx={x(hover)} cy={y(days[hover].total)} r={4} fill={SUBSCRIBED_COLOR} stroke="#fff" strokeWidth={2} />}
                {days.map((_, i) => (
                    <rect key={i} x={x(i) - width / days.length / 2} y={0} width={width / days.length} height={height} fill="transparent" onMouseEnter={() => setHover(i)} />
                ))}
            </svg>
        </Box>
    );
}
